import { api } from './client';
import type { Page } from './catalogs';

/** Кабінет звітності: журнал відправок електронних документів у ДПС. */

export type DpsStatus =
  'DRAFT' | 'SIGNED' | 'SENT' | 'DELIVERED' | 'ACCEPTED' | 'REJECTED' | 'ERROR';

export interface DpsSubmission {
  id: string;
  organization_id: string;
  source_type: string;
  source_id: string;
  form_code: string;
  form_title: string | null;
  // Період подання (як у веб-кабінеті ДПС): 1=місяць, 2=квартал.
  period_year: number | null;
  period_month: number | null;
  period_type: number | null;
  file_name: string;
  status: DpsStatus;
  dps_report_id: number | null;
  dps_status_code: string | null;
  error_message: string | null;
  kvt1_base64: string | null;
  kvt1_fname: string | null;
  kvt2_base64: string | null;
  kvt2_fname: string | null;
  kvt2_is_final: boolean;
  receipt_text: string | null;
  receipt_accepted: boolean | null;
  receipt1_xml: string | null;
  receipt2_xml: string | null;
  signer_edrpou: string | null;
  sent_at: string | null;
  delivered_at: string | null;
  finalized_at: string | null;
  created_at: string;
}

export interface DpsSubmissionCreate {
  organization_id: string;
  source_type: string;
  source_id: string;
  form_code: string;
  form_title?: string | null;
  file_name: string;
  container_base64: string;
  signer_edrpou?: string | null;
  signed_at?: string | null;
}

export interface DpsSubmitResult {
  submission: DpsSubmission;
  dps_status: string | null;
  message: string | null;
}

export const dpsSubmissions = {
  list: (params?: {
    organization_id?: string; source_type?: string; source_id?: string;
    date_from?: string; date_to?: string; limit?: number;
  }) => api.get<Page<DpsSubmission>>('/dps-submissions', { params }).then((r) => r.data),
  get: (id: string) =>
    api.get<DpsSubmission>(`/dps-submissions/${id}`).then((r) => r.data),
  submit: (p: DpsSubmissionCreate) =>
    api.post<DpsSubmitResult>('/dps-submissions', p).then((r) => r.data),
  sign: (id: string, containerBase64: string, fileName: string, signerEdrpou?: string) =>
    api.post<DpsSubmission>(`/dps-submissions/${id}/sign`, {
      container_base64: containerBase64, file_name: fileName,
      ...(signerEdrpou ? { signer_edrpou: signerEdrpou } : {}),
    }).then((r) => r.data),
  send: (id: string) =>
    api.post<DpsSubmitResult>(`/dps-submissions/${id}/send`).then((r) => r.data),
  signedContainer: (id: string) =>
    api.get<{ container_base64: string | null }>(`/dps-submissions/${id}/signed-container`).then((r) => r.data),
  refresh: (id: string, encryptedIdBase64?: string, signerEdrpou?: string) =>
    api.post<DpsSubmission>(`/dps-submissions/${id}/refresh`, {
      ...(encryptedIdBase64 ? { encrypted_id_base64: encryptedIdBase64 } : {}),
      ...(signerEdrpou ? { signer_edrpou: signerEdrpou } : {}),
    }).then((r) => r.data),
  saveReceiptText: (
    id: string, receiptText: string, accepted: boolean | null,
    receiptXml?: string, receiptNum?: 1 | 2,
  ) =>
    api.post<DpsSubmission>(`/dps-submissions/${id}/receipt-text`, {
      receipt_text: receiptText, receipt_accepted: accepted,
      ...(receiptXml ? { receipt_xml: receiptXml, receipt_num: receiptNum } : {}),
    }).then((r) => r.data),
  remove: (id: string) => api.delete(`/dps-submissions/${id}`).then(() => undefined),
};

/** Вхідний документ від ДПС (квитанція/витяг/відповідь на запит). */
export interface DpsIncoming {
  id: string;
  organization_id: string;
  submission_id: string;
  num_kvt: number;
  form_code: string | null;
  title: string | null;
  fname: string | null;
  doc_text: string | null;
  is_final: boolean;
  kvt_status: number;
  received_at: string;
  // Реквізити подачі — до якого нашого документа прийшла ця відповідь.
  submission_form_code: string | null;
  submission_form_title: string | null;
  submission_file_name: string | null;
}

export const dpsIncoming = {
  list: (params?: { organization_id?: string; submission_id?: string; limit?: number }) =>
    api.get<Page<DpsIncoming>>('/dps-submissions/incoming', { params }).then((r) => r.data),
  file: (id: string) =>
    api.get(`/dps-submissions/incoming/${id}/file`, { responseType: 'blob' }),
};

/** Email-сповіщення ДПС (вид документа + дата + посилання «дивитись»). */
export interface DpsEmailNotification {
  id: string;
  organization_id: string | null;
  recipient_code: string;
  subject: string | null;
  doc_kind: string | null;
  view_link: string | null;
  doc_datetime: string | null;
  received_at: string;
}

export const dpsEmailNotifications = {
  list: (params?: { organization_id?: string; limit?: number }) =>
    api.get<Page<DpsEmailNotification>>('/dps-submissions/email-notifications',
      { params }).then((r) => r.data),
};

/** Вхідний документ приватного API ЕК (post/incoming). */
export interface DpsPrivIncoming {
  id: number;
  cdoc: string | null;
  tin: number | null;
  dateIn: string | null;
  periodYear: number | null;
  isRead: number | null;
  status: number | null;
  text?: string | null;       // назва/зміст документа
  docName?: string | null;    // людська назва форми (якщо є)
}

export const dpsPrivApi = {
  incoming: (p: { auth_header: string; organization_id: string;
    signer_edrpou?: string; page?: number }) =>
    api.post<{
      content: DpsPrivIncoming[]; totalElements?: number;
      totalPages?: number; number?: number; last?: boolean; first?: boolean;
    }>('/dps-submissions/priv/incoming', p).then((r) => r.data),
  file: (p: { auth_header: string; organization_id: string;
    signer_edrpou?: string; year: number; doc_id: string; fmt?: 'pdf' | 'xml' }) =>
    api.post('/dps-submissions/priv/incoming/file', p,
      { responseType: 'blob' }),
  // Забрати ВСІ вхідні з кабінету і зберегти у нас (дедуп). Повертає counts.
  sync: (p: { auth_header: string; organization_id: string; signer_edrpou?: string }) =>
    api.post<{ total_in_cabinet: number; saved: number; errors: number }>(
      '/dps-submissions/priv/incoming/sync', p).then((r) => r.data),
};

/** Збережений вхідний документ кабінету ДПС (з приватного API). */
export interface DpsCabinetDoc {
  id: string;
  organization_id: string;
  dps_doc_id: string;
  cdoc: string | null;
  title: string | null;
  doc_name: string | null;
  date_in: string | null;
  period_year: number | null;
  is_read: number;
  dps_status: number | null;
  fetched_at: string;
  has_pdf: boolean;
}

export const dpsCabinetDocs = {
  list: (params?: { organization_id?: string; limit?: number }) =>
    api.get<Page<DpsCabinetDoc>>('/dps-submissions/priv/documents', { params })
      .then((r) => r.data),
  pdf: (id: string) =>
    api.get(`/dps-submissions/priv/documents/${id}/pdf`, { responseType: 'blob' }),
};

/** Збережений на сервері ключ КЕП (за згодою юзера; пароль НЕ зберігається). */
export interface KepKeyMeta {
  id: string;
  file_name: string;
  subj_cn: string | null;
  subj_org: string | null;
  issuer_cn: string | null;
  serial: string;
  edrpou: string | null;
  drfo: string | null;
}

export const kepKeys = {
  list: () => api.get<KepKeyMeta[]>('/kep-keys').then((r) => r.data),
  save: (p: {
    file_name: string; key_base64: string; serial: string;
    subj_cn?: string | null; subj_org?: string | null; issuer_cn?: string | null;
    edrpou?: string | null; drfo?: string | null;
  }) => api.post<KepKeyMeta>('/kep-keys', p).then((r) => r.data),
  blob: (id: string) =>
    api.get<{ key_base64: string; file_name: string }>(`/kep-keys/${id}/blob`)
      .then((r) => r.data),
  remove: (id: string) => api.delete(`/kep-keys/${id}`).then(() => undefined),
};

/** Поданий звіт (reg_doc/list) + файли (reg_doc/docs). */
export interface DpsRegDoc {
  codRegdoc: number;
  doc: string | null;
  cdocSub: string | null;
  cdocVer?: number | null;
  periodMonth: number | null;
  periodYear: number | null;
  csti: number | null;
  docName?: string | null;   // людська назва форми (з API)
  dget?: string | null;      // дата отримання/подання
  dterm?: string | null;     // граничний термін
  nreg?: string | null;      // реєстраційний номер
  flagName?: string | null;  // стан (прийнято тощо)
  cntAppendix?: number | null;
}

export const dpsRegDoc = {
  list: (p: { auth_header: string; organization_id: string; signer_edrpou?: string;
    period_year: number; period_month: number }) =>
    api.post<{ content: DpsRegDoc[]; totalElements?: number }>(
      '/dps-submissions/priv/reg-doc/list', p).then((r) => r.data),
  // Файли одного звіту: основний + додатки + квитанції (кожен зі своїм codRegdoc).
  files: (p: { auth_header: string; organization_id: string; signer_edrpou?: string;
    year: number; cod_regdoc: string }) =>
    api.post<DpsRegDoc[]>('/dps-submissions/priv/reg-doc/files', p).then((r) => r.data),
  file: (p: { auth_header: string; organization_id: string; signer_edrpou?: string;
    year: number; cod_regdoc: string; fmt?: 'pdf' | 'xml' }) =>
    api.post('/dps-submissions/priv/reg-doc/file', p, { responseType: 'blob' }),
};
